/**
 * /payroll/contractors — 1099 contractor management.
 *
 * Lists contractors with W-9 status. Allows adding new contractors
 * with basic info. Used before adding them as payees on manual
 * contractor paychecks.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, FormControlLabel, Switch,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AddIcon from "@mui/icons-material/Add";
import { api } from "@/lib/api";
import { queryOptions } from "@tanstack/react-query";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/contractors")({
  component: ContractorsPage,
});

interface Contractor {
  id: string;
  business_name: string | null;
  first_name: string | null;
  last_name: string | null;
  address_state: string | null;
  w9_on_file: boolean;
  is_active: boolean;
}

const contractorsQueryOptions = () =>
  queryOptions({
    queryKey: ["payroll-contractors"],
    queryFn: () => api<Contractor[]>("/api/payroll/contractors"),
  });

function displayName(c: Contractor) {
  if (c.business_name) return c.business_name;
  return [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
}

function ContractorsPage() {
  const qc = useQueryClient();
  const q = useQuery(contractorsQueryOptions());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    business_name: "", first_name: "", last_name: "",
    address_state: "", w9_on_file: false,
  });

  const addMut = useMutation({
    mutationFn: (p: typeof form) =>
      api<Contractor>("/api/payroll/contractors", {
        method: "POST",
        body: JSON.stringify({
          ...p,
          business_name: p.business_name || null,
          first_name: p.first_name || null,
          last_name: p.last_name || null,
          address_state: p.address_state || null,
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["payroll-contractors"] });
      setOpen(false);
      setForm({ business_name: "", first_name: "", last_name: "", address_state: "", w9_on_file: false });
    },
  });

  const contractors = q.data ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PeopleAltIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Contractors</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            1099 independent contractors
          </Typography>
          <Box sx={{ ml: 2 }}>
            <HelpHint docPath="payroll-l1/contractor-vs-employee.md" label="Contractor classification help" />
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Add Contractor
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
              <TableCell>Name / Business</TableCell>
              <TableCell>State</TableCell>
              <TableCell>W-9 on File</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contractors.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  No contractors yet — click Add Contractor to get started.
                </TableCell>
              </TableRow>
            )}
            {contractors.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{displayName(c)}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{c.address_state ?? "—"}</TableCell>
                <TableCell>
                  <Chip
                    label={c.w9_on_file ? "On file" : "Missing"}
                    size="small"
                    color={c.w9_on_file ? "success" : "warning"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={c.is_active ? "Active" : "Inactive"}
                    size="small"
                    color={c.is_active ? "success" : "default"}
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Contractor</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField label="Business Name" size="small" fullWidth
            value={form.business_name}
            onChange={(e) => setForm((p) => ({ ...p, business_name: e.target.value }))}
            helperText="For sole proprietors leave blank and use first/last name" />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="First Name" size="small" fullWidth
              value={form.first_name}
              onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))} />
            <TextField label="Last Name" size="small" fullWidth
              value={form.last_name}
              onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))} />
          </Box>
          <TextField label="State (address)" size="small" fullWidth
            value={form.address_state} placeholder="TX, CA, ON…"
            onChange={(e) => setForm((p) => ({ ...p, address_state: e.target.value }))} />
          <FormControlLabel
            control={<Switch checked={form.w9_on_file}
              onChange={(e) => setForm((p) => ({ ...p, w9_on_file: e.target.checked }))} />}
            label="W-9 on file" />
          {addMut.error && <Alert severity="error">{String(addMut.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={addMut.isPending}
            onClick={() => addMut.mutate(form)}>
            {addMut.isPending ? "Adding…" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
